import { useState, useCallback, ChangeEvent, useEffect } from 'react';

/**
 * Format number to Indonesian Rupiah string
 * Example: 1000000 -> "Rp 1.000.000"
 */
export function formatRupiah(value: number | string): string {
  const numericString = value.toString().replace(/[^0-9]/g, '');
  if (!numericString) return '';
  
  const numericValue = parseInt(numericString, 10);
  if (isNaN(numericValue)) return '';

  const formatted = numericValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `Rp ${formatted}`;
}

/**
 * Parse Rupiah string back to raw number
 * Example: "Rp 1.000.000" -> 1000000
 */
export function parseRupiah(value: string): number {
  const numericString = value.replace(/[^0-9]/g, '');
  return parseInt(numericString || '0', 10);
}

export function useCurrencyInput(initialValue: number = 0) {
  const [value, setValue] = useState<number>(initialValue);
  const [displayValue, setDisplayValue] = useState<string>('');

  // Sync initial value on mount or when it changes externally
  useEffect(() => {
    setValue(initialValue);
    setDisplayValue(initialValue > 0 ? formatRupiah(initialValue) : '');
  }, [initialValue]);

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    
    // If the user clears the input
    if (rawValue === '' || rawValue.toLowerCase() === 'rp' || rawValue.toLowerCase() === 'rp ' || rawValue.toLowerCase() === 'rp.') {
      setValue(0);
      setDisplayValue('');
      return;
    }

    const numericValue = parseRupiah(rawValue);
    setValue(numericValue);
    
    // Format numeric value back to display
    // using numericValue ensures leading zeros are dropped (e.g. 010000 -> 10.000)
    setDisplayValue(formatRupiah(numericValue));
  }, []);

  const setManualValue = useCallback((newValue: number) => {
    setValue(newValue);
    setDisplayValue(newValue > 0 ? formatRupiah(newValue) : '');
  }, []);

  const reset = useCallback(() => {
    setValue(0);
    setDisplayValue('');
  }, []);

  return {
    value,           // Raw numeric value (send this to API)
    displayValue,    // Formatted value (bind this to input value)
    handleChange,    // bind this to input onChange
    setManualValue,  // programmatically set value
    reset            // programmatically reset input
  };
}
